import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { FC, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uniqSavedCreditCardsSelector } from 'src/redux/savedCreditCards/selector';
import {
  CardFormWrapper,
  NewCardButton,
  NewCardTitle,
  PaymentButton,
  SavedCardsWrapper,
} from './CardFormComponent.style';
import { sortCardsByDefaultPaymentMethod } from './utils';
import { SavedCreditCards } from '../../../components/SavedCreditCards';
import { SavedCreditCardsType, StripeResponseType } from '../type';
import { getFirestoreGlobalVariablesThunk } from '../../../redux/firestoreGlobalVariables/action';
import { IPromotionalCode } from '../../types';
import { ISpecialist, StripeSupportedCurrency, SupportedCurrencies } from '../../../redux/specialists/types';
import { addUsersPsychologist } from 'src/redux/usersPsychologists/reducer';
import { Capacitor } from '@capacitor/core';
import Keyboard from 'src/api/keyboard';
import StripeCard from '../../../components/StripeCard/StripeCard';
import { FormattedMessage, useIntl } from 'react-intl';
import { ImpactStyle } from '@capacitor/haptics';
import useToast from '../../../context/ToastContext/ToastContext';
import { SupportedCurrency } from '@mindly/ui-components';
import { CouponPreviewDetailsType, useAnalytics, useAuthFlow } from '../../../shared';
import { confirmTrialSessionPayment } from '../../../api/trial-session';
import { fetchLastContract, fetchSpecialistByID } from 'src/redux/loadedSpecialists/thunk';
import { removePromoCode } from 'src/redux/promoCodes/promoCodes.reducer';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { PaymentProvidersFeature } from 'src/features/PaymentProvidersFeature';
import { useAppointment } from 'src/hooks/useAppointment/useAppointment';

type CardFormComponentProps = {
  selectedPsychologist: ISpecialist | undefined;
  info: StripeResponseType;
  promoCodeData: IPromotionalCode | null;
  setIsFetching: (s: 'success' | 'start' | null) => void;
  setPaymentStatus: (status: null | 'confirmed' | 'paid' | 'unknown') => void;
  couponPreviewDetails?: CouponPreviewDetailsType | null;
  disabled?: boolean;
  consultationTime: string;
  consultationID: string;
};

export const CardFormComponent: FC<CardFormComponentProps> = ({
  selectedPsychologist,
  info,
  promoCodeData,
  setIsFetching,
  setPaymentStatus,
  couponPreviewDetails,
  disabled,
  consultationTime,
  consultationID,
}) => {
  const intl = useIntl();
  const cardInputRef = useRef<HTMLDivElement | null>(null);

  const { createConsultationByCouponBalance } = useAppointment();
  const [choosedCreditCard, setChoosedCreditCard] = useState<SavedCreditCardsType>({
    brand: '',
    exp_month: 0,
    exp_year: 0,
    last4: '',
    paymentMethodId: '',
    isDefaultPaymentMethod: false,
  });

  const [addNewCard, setAddNewCard] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  const savedCreditCards = useSelector(uniqSavedCreditCardsSelector);

  const stripe = useStripe();
  const elements = useElements();
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const { trackPredefinedEvent } = useAnalytics();
  const { authUser } = useAuthFlow();

  const trackPaymentButtonClick = useCallback(
    (paymentType: 'card' | 'apple' | 'google' | 'balance') => {
      if (!selectedPsychologist) return;
      let paymentTypeToTrack;
      switch (paymentType) {
        case 'apple':
          paymentTypeToTrack = 'Apple Pay';
          break;
        case 'google':
          paymentTypeToTrack = 'Google Pay';
          break;
        case 'balance':
          paymentTypeToTrack = 'Balance';
          break;
        default:
          paymentTypeToTrack = 'Card';
          break;
      }
      trackPredefinedEvent('TRIAL_CHECKOUT_PAY_BUTTON_CLICKED', {
        specialist_id: selectedPsychologist.id,
        specialist_name: `${selectedPsychologist.first_name} ${selectedPsychologist.last_name}`,
        payment_type: paymentTypeToTrack,
      });
    },
    [selectedPsychologist, trackPredefinedEvent]
  );

  const chargeAmount = useMemo(() => {
    return (
      couponPreviewDetails?.payment_amount_charged ?? promoCodeData?.paymentIntent?.amount ?? info.stripeChargeAmount
    );
  }, [couponPreviewDetails?.payment_amount_charged, promoCodeData, info]);

  const createPaymentMethod = useCallback(async () => {
    const cardElements = elements?.getElement(CardElement);

    if (!cardElements)
      return showToast({
        text: 'Щось пішло не так, схоже дані про оплату не завантажились, спробуйте ще раз та зв`яжіться з тех.підтрмкою',
      });

    const { paymentMethod, error: stripeError } =
      (await stripe?.createPaymentMethod({
        type: 'card',
        card: cardElements,
      })) ?? {};

    if (stripeError) return showToast({ error: stripeError });

    return paymentMethod?.id;
  }, [stripe, elements]);

  const handleSuccessfulPayment = useCallback(() => {
    const currency = StripeSupportedCurrency.includes(info?.priceType) ? info?.priceType : SupportedCurrencies.EUR;
    const specialistName = `${selectedPsychologist?.first_name} ${selectedPsychologist?.last_name}`;
    const specialistId = selectedPsychologist?.id;
    const platform = Capacitor.getPlatform();

    trackPredefinedEvent('purchase', {
      currency: currency,
      value: chargeAmount / 100,
      specialist_name: specialistName,
      specialist_id: specialistId,
      platform: platform,
    });

    setPaymentStatus('confirmed');
  }, [chargeAmount, info, selectedPsychologist]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!stripe || !elements)
        return showToast({
          text: intl.formatMessage({
            id: 'booking.error.stripe_not_loaded_yet',
            defaultMessage:
              'Сервіс для оплати ще не загрузився, спробуйте сплатити через 10 секунд, або перезагрузіть сторінку.',
          }),
        });

      setIsFetching('start');
      trackPaymentButtonClick('card');
      const paymentMethod = choosedCreditCard?.paymentMethodId?.length
        ? choosedCreditCard?.paymentMethodId
        : await createPaymentMethod();

      if (!paymentMethod) {
        setIsFetching(null);
        return showToast({
          text: intl.formatMessage({
            id: 'payment.error.payment_method_not_created',
            defaultMessage: 'Не вірні реквізити картки, перевірте їх правильність та спробуйте знову.',
          }),
        });
      }

      try {
        await dispatch(getFirestoreGlobalVariablesThunk());

        await confirmTrialSessionPayment({
          paymentIntentId: info.paymentIntentID,
          couponPreviewDetails,
        });

        if (selectedPsychologist) {
          await dispatch(addUsersPsychologist(selectedPsychologist));
          await dispatch(fetchSpecialistByID(selectedPsychologist.id));
          await dispatch(fetchLastContract(selectedPsychologist.id));
        }
      } catch (error: any) {
        setIsFetching(null);
        return showToast({ error });
      }

      const payload = await stripe.confirmCardPayment(info.secret, {
        payment_method: paymentMethod,
      });

      if (payload?.error) {
        showToast({ error: payload.error });
        setIsFetching(null);
      } else {
        if (promoCodeData?.code) {
          await dispatch(removePromoCode(promoCodeData?.code));
        }
        handleSuccessfulPayment();
      }
    },
    [stripe, elements, promoCodeData, choosedCreditCard, showToast, couponPreviewDetails, dispatch]
  );

  const handleFocus = () => {
    setIsDisabled(false);
    setChoosedCreditCard({
      brand: '',
      exp_month: 0,
      exp_year: 0,
      last4: '',
      paymentMethodId: '',
      isDefaultPaymentMethod: false,
    });
  };

  useEffect(() => {
    Keyboard?.addListener('keyboardDidShow', () => {
      if (document.activeElement?.tagName === 'IFRAME')
        cardInputRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'start',
        });
    });

    return () => {
      Keyboard?.removeAllListeners();
    };
  }, []);

  const onCouponBalanceClick = useCallback(async () => {
    try {
      setIsFetching('start');
      trackPaymentButtonClick('balance');

      const userID = authUser?.uid;
      const specialistID = selectedPsychologist?.id;
      const bookingTimeUTCInISO = consultationTime;

      if (!userID || !specialistID || !bookingTimeUTCInISO) {
        return showToast({ error: intl.formatMessage({ id: 'booking.error.stripe_not_loaded_yet' }) });
      }

      isomorficHapticsImpact({ style: ImpactStyle.Light });
      await createConsultationByCouponBalance(userID, specialistID, bookingTimeUTCInISO, consultationID);
      handleSuccessfulPayment();
    } catch (error) {
      setIsFetching(null);
      showToast({ error });
    }
  }, [createConsultationByCouponBalance, selectedPsychologist?.id, consultationTime, consultationID]);

  if (couponPreviewDetails?.coupon_type === 'balance' && couponPreviewDetails?.payment_amount_final === 0) {
    return (
      <div className="px-4">
        <PaymentButton buttonType="primary" disabled={false} onClick={onCouponBalanceClick}>
          <FormattedMessage id={'card.pay'} defaultMessage={'Сплатити'} />
        </PaymentButton>
      </div>
    );
  }

  return (
    <CardFormWrapper>
      <PaymentProvidersFeature
        stripeChargeCurrency={info.stripeChargeCurrency as unknown as SupportedCurrency}
        stripeChargeAmount={chargeAmount}
        label="Therapy session"
        disabled={disabled}
        onSuccess={() => {
          setIsFetching('start');
          handleSuccessfulPayment();
        }}
        onClick={(type) => {
          trackPaymentButtonClick(type);
        }}
        confirmPayment={async () => {
          const result = await confirmTrialSessionPayment({
            paymentIntentId: info.paymentIntentID,
            couponPreviewDetails,
          });
          return result;
        }}
      />

      {!!savedCreditCards?.length && (
        <SavedCardsWrapper>
          <SavedCreditCards
            savedCreditCards={sortCardsByDefaultPaymentMethod(savedCreditCards)}
            setChoosedCreditCard={setChoosedCreditCard}
            choosedCreditCard={choosedCreditCard}
          />
        </SavedCardsWrapper>
      )}
      {!addNewCard && !!savedCreditCards?.length && (
        <NewCardButton onClick={() => setAddNewCard(true)} disabled={disabled}>
          <FormattedMessage id={'card.pay.new'} defaultMessage={'Або оплатити новою карткою'} /> <span>+</span>
        </NewCardButton>
      )}
      <form onSubmit={handleSubmit} id="stripe-card-element">
        {(savedCreditCards?.length === 0 || addNewCard) && (
          <>
            {!!savedCreditCards?.length && (
              <NewCardTitle>
                <FormattedMessage id={'card.pay.new'} defaultMessage={'Або оплатити новою карткою'} />
              </NewCardTitle>
            )}
            <StripeCard
              ref={cardInputRef}
              onFocus={handleFocus}
              onChange={(e) => {
                setIsDisabled(!e.complete);
              }}
            />
          </>
        )}

        <PaymentButton
          buttonType="primary"
          disabled={(isDisabled && !choosedCreditCard?.paymentMethodId) || disabled}
          onClick={() => isomorficHapticsImpact({ style: ImpactStyle.Light })}
        >
          <FormattedMessage id={'card.pay'} defaultMessage={'Сплатити'} />
        </PaymentButton>
      </form>
    </CardFormWrapper>
  );
};
